import React from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  dashboardProjectKey,
  dashboardProjectSwitchPath,
} from "../../utils/dashboardNavigation";
import DashboardNavigationIcon from "./DashboardNavigationIcon";
import GlobalActivityBell from "./GlobalActivityBell";
import { useWebProfitThemeMode } from "../../theme/webProfitTheme";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

function projectMatchesKey(project, key) {
  if (!project || !key) return false;
  return [project.slug, project.id, project._id]
    .filter(Boolean)
    .some((value) => String(value) === String(key));
}

export default function DashboardTopGlobalControls({
  navigation,
  project = null,
  projects = [],
  setActiveProject,
  compact = false,
}) {
  const { user, logout } = useAuth();
  const { mode: panelThemeMode, toggleMode: togglePanelTheme } = useWebProfitThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountAnchor, setAccountAnchor] = React.useState(null);
  const accountOpen = Boolean(accountAnchor);
  const accountTools = navigation?.global?.accountTools || [];
  const administrationTools = navigation?.global?.administrationTools || [];
  const ownerActive = Boolean(user?.isPlatformOwner && location.pathname.startsWith("/dashboard/owner"));
  const accountActive = ownerActive || [...accountTools, ...administrationTools].some((tool) => tool.active);
  const activeProjectKey = dashboardProjectKey(project);

  const handleProjectChange = (event) => {
    const nextKey = String(event.target.value || "");
    if (!nextKey || projectMatchesKey(project, nextKey)) return;

    const nextProject = projects.find((candidate) => projectMatchesKey(candidate, nextKey));
    if (!nextProject) return;

    const nextPathname = dashboardProjectSwitchPath(location.pathname, nextProject);
    if (nextPathname !== location.pathname) {
      // En rutas de proyecto la URL sigue siendo la autoridad: navegar primero
      // evita que el sincronizador del shell restaure el proyecto anterior.
      navigate({
        pathname: nextPathname,
        search: location.search,
        hash: location.hash,
      });
      return;
    }

    // En pantallas globales no hace falta abandonar el contexto actual.
    setActiveProject?.(nextProject);
  };

  const goGlobalTool = (tool) => {
    if (!tool?.path) return;
    setAccountAnchor(null);
    navigate(tool.path);
  };

  const handleLogout = async () => {
    setAccountAnchor(null);
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={0.65}
      aria-label="Controles globales del panel"
      sx={{ minWidth: 0 }}
    >
      {user?.isPlatformOwner ? (
        <Tooltip title={ownerActive ? "Jarvis · Owner 360 activo" : "Abrir Jarvis · Owner 360"} arrow enterDelay={250}>
          <IconButton
            type="button"
            aria-label="Abrir Jarvis Owner 360"
            aria-current={ownerActive ? "page" : undefined}
            onClick={() => navigate("/dashboard/owner")}
            sx={{
              width: compact ? 38 : 40,
              height: compact ? 38 : 40,
              color: ownerActive ? BRAND.orange : BRAND.dark,
              border: ownerActive ? "1px solid rgba(243,90,58,.34)" : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
              bgcolor: ownerActive ? "rgba(243,90,58,.10)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
              boxShadow: ownerActive ? "0 0 0 3px rgba(243,90,58,.055)" : "none",
              "&:hover": { bgcolor: ownerActive ? "rgba(243,90,58,.15)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" },
            }}
          >
            <RecordVoiceOverRoundedIcon sx={{ fontSize: compact ? 20 : 21 }} />
          </IconButton>
        </Tooltip>
      ) : null}

      {projects.length && !ownerActive ? (
        <Tooltip
          title={project?.name ? `Proyecto activo: ${project.name}` : "Elegir proyecto"}
          arrow
          enterDelay={350}
        >
          <Select
            size="small"
            value={activeProjectKey}
            onChange={handleProjectChange}
            displayEmpty
            IconComponent={KeyboardArrowDownRoundedIcon}
            inputProps={{ "aria-label": "Proyecto activo" }}
            renderValue={() => (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  noWrap
                  sx={{
                    display: "block",
                    maxWidth: compact ? { xs: "calc(100% - 2px)", sm: 138 } : 154,
                    fontSize: compact ? ".74rem" : ".78rem",
                    fontWeight: 900,
                    lineHeight: 1.2,
                  }}
                >
                  {project?.name || "Elegir proyecto"}
                </Typography>
              </Box>
            )}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: .7,
                  minWidth: { xs: 220, sm: 260 },
                  width: { xs: "min(320px, calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right)))", sm: 320 },
                  maxWidth: "calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right))",
                  maxHeight: "min(420px, calc(100dvh - 96px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))",
                  borderRadius: 2.5,
                  border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
                  boxShadow: "0 18px 46px var(--wp-panel-shadow, rgba(var(--wp-panel-shadow-rgb, 25,36,47),.16))",
                },
              },
              MenuListProps: {
                "aria-label": "Cambiar proyecto activo",
                sx: { py: .65 },
              },
            }}
            sx={{
              width: compact ? { xs: "clamp(104px, 31vw, 132px)", sm: 176 } : undefined,
              minWidth: compact ? 0 : { md: 142, lg: 176 },
              maxWidth: compact ? { xs: 132, sm: 190 } : 205,
              height: compact ? 38 : 40,
              color: BRAND.dark,
              bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.045)",
              borderRadius: 2.25,
              "& .MuiSelect-select": { py: 1, pl: 1.25, pr: "34px !important" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.18)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.orange, borderWidth: 2 },
            }}
          >
            {projects.map((candidate) => {
              const key = dashboardProjectKey(candidate);
              return (
                <MenuItem
                  key={key}
                  value={key}
                  sx={{
                    minHeight: 44,
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: 1.3,
                    fontSize: ".84rem",
                    fontWeight: 800,
                  }}
                >
                  <Typography component="span" sx={{ minWidth: 0, fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit", overflowWrap: "anywhere" }}>
                    {candidate.name || key}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </Tooltip>
      ) : null}

      <GlobalActivityBell mobile={compact} projectOverride={project} />

      {!compact ? (
        <Tooltip
        title={panelThemeMode === "dark" ? "Usar tema claro" : "Usar tema oscuro"}
        arrow
        enterDelay={350}
      >
        <IconButton
          type="button"
          aria-label={panelThemeMode === "dark" ? "Activar tema claro" : "Activar tema oscuro"}
          aria-pressed={panelThemeMode === "dark"}
          onClick={togglePanelTheme}
          sx={{
            width: compact ? 38 : 40,
            height: compact ? 38 : 40,
            color: "var(--wp-panel-text-primary, #19242F)",
            border: "1px solid var(--wp-panel-border, rgba(var(--wp-panel-ink-rgb, 25,36,47),.09))",
            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
            "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" },
          }}
        >
          {panelThemeMode === "dark" ? (
            <LightModeRoundedIcon sx={{ fontSize: compact ? 20 : 21 }} />
          ) : (
            <DarkModeRoundedIcon sx={{ fontSize: compact ? 20 : 21 }} />
          )}
        </IconButton>
        </Tooltip>
      ) : null}

      <Tooltip title={administrationTools.length ? "Cuenta y administración" : "Cuenta"} arrow enterDelay={350}>
        <IconButton
          type="button"
          aria-label={administrationTools.length ? "Abrir opciones de cuenta y administración" : "Abrir opciones de cuenta"}
          aria-haspopup="menu"
          aria-expanded={accountOpen ? "true" : undefined}
          onClick={(event) => setAccountAnchor(event.currentTarget)}
          sx={{
            width: compact ? 38 : 40,
            height: compact ? 38 : 40,
            p: 0.35,
            border: accountActive ? `2px solid ${BRAND.orange}` : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
            bgcolor: accountActive ? "rgba(243,90,58,.08)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)",
            "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" },
          }}
        >
          <Avatar sx={{ width: compact ? 29 : 31, height: compact ? 29 : 31, bgcolor: BRAND.orange, fontSize: compact ? 12 : 13, fontWeight: 950 }}>
            {user?.name?.[0]?.toUpperCase() || "W"}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={accountAnchor}
        open={accountOpen}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 268,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: 2.5,
            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
            boxShadow: "0 18px 46px var(--wp-panel-shadow, rgba(var(--wp-panel-shadow-rgb, 25,36,47),.16))",
          },
        }}
        MenuListProps={{ "aria-label": administrationTools.length ? "Opciones de cuenta y administración" : "Opciones de cuenta" }}
      >
        <Box sx={{ px: 2, py: 1.2, minWidth: 0 }}>
          <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".88rem" }}>
            {user?.name || "Usuario"}
          </Typography>
          <Typography noWrap variant="caption" sx={{ display: "block", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 700 }}>
            {user?.email || "Cuenta WebProfit"}
          </Typography>
        </Box>
        <Divider />
        {user?.isPlatformOwner ? (
          <>
            <Typography
              component="div"
              variant="caption"
              sx={{ px: 2, pt: 1.15, pb: 0.45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 950, letterSpacing: ".05em" }}
            >
              OWNER PRIVADO
            </Typography>
            <MenuItem
              selected={ownerActive}
              onClick={() => { setAccountAnchor(null); navigate("/dashboard/owner"); }}
              sx={{ py: 1.1 }}
            >
              <WorkspacePremiumRoundedIcon sx={{ mr: 1.2, fontSize: 20, color: BRAND.orange }} />
              <Typography sx={{ fontSize: ".84rem", fontWeight: 950 }}>Mi centro personal</Typography>
            </MenuItem>
          </>
        ) : null}
        {accountTools.map((tool) => (
          <MenuItem
            key={tool.key}
            selected={Boolean(tool.active)}
            onClick={() => goGlobalTool(tool)}
            sx={{ py: 1.1 }}
          >
            <DashboardNavigationIcon name={tool.iconKey} sx={{ mr: 1.2, fontSize: 20 }} />
            <Typography sx={{ fontSize: ".84rem", fontWeight: 850 }}>
              {tool.label}
            </Typography>
          </MenuItem>
        ))}
        {administrationTools.length > 0 ? <Divider /> : null}
        {administrationTools.length > 0 ? (
          <Typography
            component="div"
            variant="caption"
            sx={{ px: 2, pt: 1.15, pb: 0.45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 950, letterSpacing: ".05em" }}
          >
            SUPERADMINISTRACIÓN
          </Typography>
        ) : null}
        {administrationTools.map((tool) => (
          <MenuItem
            key={tool.key}
            selected={Boolean(tool.active)}
            onClick={() => goGlobalTool(tool)}
            sx={{ py: 1.1 }}
          >
            <DashboardNavigationIcon name={tool.iconKey} sx={{ mr: 1.2, fontSize: 20 }} />
            <Typography sx={{ fontSize: ".84rem", fontWeight: 850 }}>
              {tool.label}
            </Typography>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            togglePanelTheme();
            setAccountAnchor(null);
          }}
          sx={{ py: 1.1 }}
        >
          {panelThemeMode === "dark" ? (
            <LightModeRoundedIcon sx={{ mr: 1.2, fontSize: 20 }} />
          ) : (
            <DarkModeRoundedIcon sx={{ mr: 1.2, fontSize: 20 }} />
          )}
          <Typography sx={{ fontSize: ".84rem", fontWeight: 850 }}>
            {panelThemeMode === "dark" ? "Tema claro" : "Tema oscuro"}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.1 }}>
          <LogoutRoundedIcon sx={{ mr: 1.2, fontSize: 20 }} />
          <Typography sx={{ fontSize: ".84rem", fontWeight: 850 }}>Cerrar sesión</Typography>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
